import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import { useFundingRadarStore } from "../store/fundingRadarStore";

const PROGRAMMES = ["ALL", "HORIZON", "DIGITAL", "LIFE", "ERASMUS", "CEF"] as const;

function parseCsv(s: string): string[] {
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function formatEuro(v?: number): string {
  if (v == null) return "n/a";
  return new Intl.NumberFormat("hu-HU", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
}

export default function FundingRadar() {
  const { t } = useTranslation();
  const {
    calls,
    lastSyncAt,
    syncInProgress,
    filterStatus,
    filterProgramme,
    filterKeyword,
    relevanceScores,
    matchProfile,
    stats,
    syncCalls,
    loadCachedCalls,
    runMatch,
    loadStats,
    setFilterStatus,
    setFilterProgramme,
    setFilterKeyword,
    setMatchProfile,
    exportFundingList,
  } = useFundingRadarStore();

  const [error, setError] = useState<Error | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [kwCsv, setKwCsv] = useState(matchProfile.keywords.join(", "));
  const [cpvCsv, setCpvCsv] = useState(matchProfile.cpvs.join(", "));
  const [budgetMin, setBudgetMin] = useState(matchProfile.budgetMin?.toString() ?? "");
  const [budgetMax, setBudgetMax] = useState(matchProfile.budgetMax?.toString() ?? "");

  const topMatches = useMemo(() => calls.slice(0, 10), [calls]);

  const handleSync = async () => {
    setError(null);
    try {
      await syncCalls({
        keyword: filterKeyword || undefined,
        programme: filterProgramme,
        status: filterStatus,
      });
      await loadCachedCalls({ status: filterStatus, programme: filterProgramme, keyword: filterKeyword, limit: 120 });
      await loadStats();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(t("fundingRadar.errorSync")));
    }
  };

  const handleMatch = async () => {
    setError(null);
    try {
      setMatchProfile({
        cpvs: parseCsv(cpvCsv),
        keywords: parseCsv(kwCsv),
        budgetMin: budgetMin ? Number(budgetMin) : undefined,
        budgetMax: budgetMax ? Number(budgetMax) : undefined,
      });
      await runMatch();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(t("fundingRadar.errorMatch")));
    }
  };

  const statusLabel = (s: string): string => {
    if (s === "open") return t("fundingRadar.statusOpen");
    if (s === "forthcoming") return t("fundingRadar.statusForthcoming");
    return t("fundingRadar.statusClosed");
  };

  const scoreColor = (score: number): string =>
    score >= 80 ? "text-green-400 border-green-500/50" : score >= 50 ? "text-yellow-400 border-yellow-500/50" : "text-red-400 border-red-500/50";

  return (
    <div className="space-y-6">
      <h2 className="font-orbitron text-evat-neon text-xl">{t("fundingRadar.title")}</h2>
      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      <section className="border border-evat-gold/30 rounded-lg p-4 bg-black/40 space-y-4">
        <h3 className="font-orbitron text-evat-gold">{t("fundingRadar.liveCallsTitle")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.filterStatus")}</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2">
              <option value="all">{t("fundingRadar.statusAll")}</option>
              <option value="open">{t("fundingRadar.statusOpen")}</option>
              <option value="forthcoming">{t("fundingRadar.statusForthcoming")}</option>
              <option value="closed">{t("fundingRadar.statusClosed")}</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.filterProgramme")}</label>
            <select value={filterProgramme} onChange={(e) => setFilterProgramme(e.target.value)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2">
              {PROGRAMMES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.filterKeyword")}</label>
            <input value={filterKeyword} onChange={(e) => setFilterKeyword(e.target.value)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2" />
          </div>
          <div className="flex items-end">
            <button onClick={handleSync} disabled={syncInProgress} className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded font-orbitron">
              {syncInProgress ? t("fundingRadar.syncing") : t("fundingRadar.syncButton")}
            </button>
          </div>
        </div>
        {lastSyncAt && <p className="text-xs text-neutral-500">{t("fundingRadar.lastSync")}: {new Date(lastSyncAt).toLocaleString()}</p>}
        {syncInProgress && <LoadingSpinner text={t("fundingRadar.syncing")} />}

        {calls.length === 0 ? (
          <p className="text-sm text-neutral-500">{t("fundingRadar.noResults")}</p>
        ) : (
          <div className="overflow-x-auto border border-evat-gold/20 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-black/70 text-xs uppercase text-neutral-400">
                <tr>
                  <th className="p-2 text-left">{t("fundingRadar.callId")}</th>
                  <th className="p-2 text-left">{t("fundingRadar.callTitle")}</th>
                  <th className="p-2 text-left">{t("fundingRadar.programme")}</th>
                  <th className="p-2 text-left">{t("fundingRadar.filterStatus")}</th>
                  <th className="p-2 text-left">{t("fundingRadar.deadline")}</th>
                  <th className="p-2 text-left">{t("fundingRadar.budget")}</th>
                  <th className="p-2 text-left">{t("fundingRadar.viewDetails")}</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <Fragment key={c.callId}>
                    <tr key={c.callId} className="border-t border-evat-gold/10">
                      <td className="p-2 font-mono">{c.callId}</td>
                      <td className="p-2 max-w-[24rem] truncate">{c.title}</td>
                      <td className="p-2">{c.programme}</td>
                      <td className="p-2">
                        <span className="px-2 py-0.5 border rounded border-evat-gold/40">{statusLabel(c.status)}</span>
                      </td>
                      <td className="p-2">{c.deadline || "n/a"}</td>
                      <td className="p-2">{formatEuro(c.budget)}</td>
                      <td className="p-2">
                        <button onClick={() => setExpanded(expanded === c.callId ? null : c.callId)} className="px-2 py-1 border rounded border-evat-gold/40 text-evat-gold">
                          {t("fundingRadar.viewDetails")}
                        </button>
                      </td>
                    </tr>
                    {expanded === c.callId && (
                      <tr className="border-t border-evat-gold/5 bg-black/40">
                        <td className="p-2" colSpan={7}>
                          <div className="text-sm text-neutral-300 space-y-1">
                            <p>{c.description || "n/a"}</p>
                            <p>{t("fundingRadar.budgetPerProject")}: {formatEuro(c.budgetPerProject)}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="border border-evat-gold/30 rounded-lg p-4 bg-black/40 space-y-4">
        <h3 className="font-orbitron text-evat-gold">{t("fundingRadar.matchTitle")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.matchKeywords")}</label>
            <input value={kwCsv} onChange={(e) => setKwCsv(e.target.value)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2" />
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.matchCpvs")}</label>
            <input value={cpvCsv} onChange={(e) => setCpvCsv(e.target.value)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2" />
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.matchBudgetMin")}</label>
            <input type="number" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2" />
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("fundingRadar.matchBudgetMax")}</label>
            <input type="number" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2" />
          </div>
        </div>
        <button onClick={handleMatch} className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded font-orbitron">
          {t("fundingRadar.runMatch")}
        </button>
        <div className="space-y-2">
          {topMatches.map((c) => {
            const rel = relevanceScores[c.callId];
            if (!rel) return null;
            return (
              <div key={c.callId} className="border border-evat-gold/20 rounded p-2 flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-neutral-100">{c.title}</div>
                  <div className="text-xs text-neutral-500">{rel.reasons.join(" · ")}</div>
                </div>
                <span className={`px-2 py-1 rounded border text-xs ${scoreColor(rel.score)}`}>
                  {t("fundingRadar.relevanceScore")}: {rel.score}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border border-evat-gold/30 rounded-lg p-4 bg-black/40 space-y-3">
        <h3 className="font-orbitron text-evat-gold">{t("fundingRadar.statsTitle")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-evat-gold/20 rounded p-3">
            <div className="text-xs text-neutral-400">{t("fundingRadar.openCalls")}</div>
            <div className="text-xl font-orbitron text-green-400">{stats.open}</div>
          </div>
          <div className="border border-evat-gold/20 rounded p-3">
            <div className="text-xs text-neutral-400">{t("fundingRadar.forthcomingCalls")}</div>
            <div className="text-xl font-orbitron text-yellow-400">{stats.forthcoming}</div>
          </div>
          <div className="border border-evat-gold/20 rounded p-3">
            <div className="text-xs text-neutral-400">{t("fundingRadar.closedCalls")}</div>
            <div className="text-xl font-orbitron text-red-400">{stats.closed}</div>
          </div>
        </div>
      </section>

      <div className="fixed bottom-4 right-4">
        <button onClick={exportFundingList} className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded font-orbitron">
          {t("fundingRadar.export")}
        </button>
      </div>
    </div>
  );
}

